// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Button,
  Tile,
  Modal,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  DatePicker,
  DatePickerInput,
  NumberInput,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  InlineNotification,
  Loading,
  OverflowMenu,
  OverflowMenuItem,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  FileUploader,
  Checkbox,
  RadioButtonGroup,
  RadioButton,
} from '@carbon/react';
import {
  DocumentAdd,
  View,
  Edit,
  Delete,
  Copy,
  Calendar,
  Time,
  Upload,
  Download,
  Send,
  CheckmarkOutline,
} from '@carbon/icons-react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

interface Assignment {
  assignment_id: string;
  title: string;
  description: string;
  instructions: string;
  subject: string;
  grade_level: string;
  assignment_type: string;
  points: number;
  due_date: string;
  start_date: string;
  status: 'draft' | 'published' | 'archived';
  submission_type: string;
  allow_late: boolean;
  attachments: string[];
  created_at: string;
  submissions_count: number;
  class_ids: string[];
}

interface Submission {
  submission_id: string;
  student_id: string;
  student_name: string;
  submitted_at: string;
  status: 'submitted' | 'late' | 'graded' | 'returned';
  score?: number;
  feedback?: string;
  attachments?: string[];
}

export const TeacherAssignmentBuilder: React.FC = () => {
  const { user, token } = useAuthStore();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    instructions: '',
    subject: 'math',
    grade_level: '9',
    assignment_type: 'homework',
    points: 100,
    due_date: '',
    start_date: new Date().toISOString().split('T')[0],
    submission_type: 'online',
    allow_late: true,
    class_ids: [] as string[],
    attachments: [] as File[],
  });

  // Load assignments on mount
  useEffect(() => {
    loadAssignments();
  }, [token]);

  const loadAssignments = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/v1/teacher/assignments`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setAssignments(response.data.assignments || []);
      }
    } catch (err: any) {
      console.error('Failed to load assignments:', err);
      setError('Failed to load assignments');
      // Create mock data for demonstration
      setAssignments([
        {
          assignment_id: '1',
          title: 'Algebra Quiz - Chapter 5',
          description: 'Test your understanding of linear equations',
          instructions: 'Complete all problems showing your work',
          subject: 'math',
          grade_level: '9',
          assignment_type: 'quiz',
          points: 100,
          due_date: '2025-10-01',
          start_date: '2025-09-25',
          status: 'published',
          submission_type: 'online',
          allow_late: false,
          attachments: [],
          created_at: '2025-09-20',
          submissions_count: 15,
          class_ids: ['math-101-p1'],
        },
        {
          assignment_id: '2',
          title: 'Geometry Homework - Week 3',
          description: 'Practice problems on triangles and angles',
          instructions: 'Complete problems 1-20 from the textbook',
          subject: 'math',
          grade_level: '10',
          assignment_type: 'homework',
          points: 50,
          due_date: '2025-09-28',
          start_date: '2025-09-25',
          status: 'published',
          submission_type: 'online',
          allow_late: true,
          attachments: [],
          created_at: '2025-09-22',
          submissions_count: 22,
          class_ids: ['math-101-p3'],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const createAssignment = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const formDataToSend = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'attachments') {
          formData.attachments.forEach(file => {
            formDataToSend.append('attachments', file);
          });
        } else if (key === 'class_ids') {
          formDataToSend.append(key, JSON.stringify(formData[key]));
        } else {
          formDataToSend.append(key, String(formData[key as keyof typeof formData]));
        }
      });

      const response = await axios.post(`${API_BASE_URL}/v1/teacher/assignments/create`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setSuccess('Assignment created successfully');
        setShowCreateModal(false);
        loadAssignments();
        resetForm();
      }
    } catch (err: any) {
      console.error('Failed to create assignment:', err);
      // For demonstration, add to local state
      const newAssignment: Assignment = {
        assignment_id: Date.now().toString(),
        ...formData,
        status: 'published',
        attachments: [],
        created_at: new Date().toISOString(),
        submissions_count: 0,
        instructions: formData.instructions || formData.description,
      };
      setAssignments([newAssignment, ...assignments]);
      setSuccess('Assignment created successfully (demo mode)');
      setShowCreateModal(false);
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  const deleteAssignment = async (assignmentId: string) => {
    if (!token) return;

    if (!confirm('Are you sure you want to delete this assignment?')) return;

    setLoading(true);
    try {
      await axios.delete(`${API_BASE_URL}/v1/teacher/assignments/${assignmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAssignments(assignments.filter(a => a.assignment_id !== assignmentId));
      setSuccess('Assignment deleted successfully');
    } catch (err) {
      console.error('Failed to delete assignment:', err);
      // For demonstration, remove from local state
      setAssignments(assignments.filter(a => a.assignment_id !== assignmentId));
      setSuccess('Assignment deleted successfully (demo mode)');
    } finally {
      setLoading(false);
    }
  };

  const duplicateAssignment = (assignment: Assignment) => {
    setFormData({
      title: `Copy of ${assignment.title}`,
      description: assignment.description,
      instructions: assignment.instructions,
      subject: assignment.subject,
      grade_level: assignment.grade_level,
      assignment_type: assignment.assignment_type,
      points: assignment.points,
      due_date: '',
      start_date: new Date().toISOString().split('T')[0],
      submission_type: assignment.submission_type,
      allow_late: assignment.allow_late,
      class_ids: assignment.class_ids,
      attachments: [],
    });
    setShowCreateModal(true);
  };

  const viewSubmissions = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    // Load submissions (mock data for demo)
    setSubmissions([
      {
        submission_id: '1',
        student_id: 'student1',
        student_name: 'John Doe',
        submitted_at: '2025-09-26T14:30:00',
        status: 'graded',
        score: 95,
        feedback: 'Excellent work!',
        attachments: [],
      },
      {
        submission_id: '2',
        student_id: 'student2',
        student_name: 'Jane Smith',
        submitted_at: '2025-09-27T10:15:00',
        status: 'submitted',
        attachments: [],
      },
      {
        submission_id: '3',
        student_id: 'student3',
        student_name: 'Bob Johnson',
        submitted_at: '2025-09-28T16:45:00',
        status: 'late',
        attachments: [],
      },
    ]);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      instructions: '',
      subject: 'math',
      grade_level: '9',
      assignment_type: 'homework',
      points: 100,
      due_date: '',
      start_date: new Date().toISOString().split('T')[0],
      submission_type: 'online',
      allow_late: true,
      class_ids: [],
      attachments: [],
    });
  };

  const headers = [
    { key: 'title', header: 'Title' },
    { key: 'subject', header: 'Subject' },
    { key: 'type', header: 'Type' },
    { key: 'due_date', header: 'Due Date' },
    { key: 'submissions', header: 'Submissions' },
    { key: 'status', header: 'Status' },
    { key: 'actions', header: 'Actions' },
  ];

  const rows = assignments.map(assignment => ({
    id: assignment.assignment_id,
    title: assignment.title,
    subject: assignment.subject,
    type: assignment.assignment_type,
    due_date: new Date(assignment.due_date).toLocaleDateString(),
    submissions: `${assignment.submissions_count} submitted`,
    status: assignment.status,
    actions: assignment,
  }));

  const submissionHeaders = [
    { key: 'student', header: 'Student' },
    { key: 'submitted', header: 'Submitted' },
    { key: 'status', header: 'Status' },
    { key: 'score', header: 'Score' },
    { key: 'actions', header: 'Actions' },
  ];

  const submissionRows = submissions.map(submission => ({
    id: submission.submission_id,
    student: submission.student_name,
    submitted: new Date(submission.submitted_at).toLocaleString(),
    status: submission.status,
    score: submission.score ? `${submission.score}/${selectedAssignment?.points}` : 'Not graded',
    actions: submission,
  }));

  return (
    <Grid fullWidth>
      <Column span={16}>
        <div
          style={{
            padding: '2rem',
            backgroundColor: 'var(--cds-background)',
            minHeight: '100vh',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <div>
              <h1
                style={{
                  fontSize: '2.5rem',
                  fontWeight: 600,
                  marginBottom: '0.5rem',
                  color: 'var(--cds-text-primary)',
                }}
              >
                Assignment Builder
              </h1>
              <p
                style={{
                  fontSize: '1.125rem',
                  color: 'var(--cds-text-secondary)',
                }}
              >
                Create, manage, and distribute assignments with automated workflow tracking.
              </p>
            </div>
            <Button kind="primary" size="lg" renderIcon={DocumentAdd} onClick={() => setShowCreateModal(true)}>
              Create New Assignment
            </Button>
          </div>

          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onClose={() => setError(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {success && (
            <InlineNotification
              kind="success"
              title="Success"
              subtitle={success}
              onClose={() => setSuccess(null)}
              style={{ marginBottom: '1rem' }}
            />
          )}

          {loading && <Loading />}

          <Tabs selectedIndex={activeTab} onChange={({ selectedIndex }) => setActiveTab(selectedIndex!)}>
            <TabList>
              <Tab>Active Assignments</Tab>
              <Tab>Drafts</Tab>
              <Tab>Archived</Tab>
              {selectedAssignment && <Tab>Submissions</Tab>}
            </TabList>
            <TabPanels>
              <TabPanel>
                <DataTable rows={rows.filter(r => r.status === 'published')} headers={headers}>
                  {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                    <TableContainer title="Active Assignments">
                      <Table {...getTableProps()}>
                        <TableHead>
                          <TableRow>
                            {headers.map(header => (
                              <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                {header.header}
                              </TableHeader>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map(row => (
                            <TableRow {...getRowProps({ row })} key={row.id}>
                              {row.cells.map(cell => {
                                if (cell.info.header === 'status') {
                                  return (
                                    <TableCell key={cell.id}>
                                      <Tag type={cell.value === 'published' ? 'green' : 'gray'}>{cell.value}</Tag>
                                    </TableCell>
                                  );
                                }
                                if (cell.info.header === 'actions') {
                                  return (
                                    <TableCell key={cell.id}>
                                      <OverflowMenu aria-label="Actions">
                                        <OverflowMenuItem
                                          itemText="View Submissions"
                                          onClick={() => viewSubmissions(cell.value as Assignment)}
                                        />
                                        <OverflowMenuItem
                                          itemText="Edit"
                                          onClick={() => {
                                            setSelectedAssignment(cell.value as Assignment);
                                            // Implement edit functionality
                                          }}
                                        />
                                        <OverflowMenuItem
                                          itemText="Duplicate"
                                          onClick={() => duplicateAssignment(cell.value as Assignment)}
                                        />
                                        <OverflowMenuItem
                                          itemText="Delete"
                                          isDelete
                                          onClick={() => deleteAssignment((cell.value as Assignment).assignment_id)}
                                        />
                                      </OverflowMenu>
                                    </TableCell>
                                  );
                                }
                                return <TableCell key={cell.id}>{cell.value}</TableCell>;
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </DataTable>
              </TabPanel>
              <TabPanel>
                <Tile>
                  <h3>No draft assignments</h3>
                  <p>Draft assignments will appear here before publishing.</p>
                </Tile>
              </TabPanel>
              <TabPanel>
                <Tile>
                  <h3>No archived assignments</h3>
                  <p>Archived assignments will appear here.</p>
                </Tile>
              </TabPanel>
              {selectedAssignment && (
                <TabPanel>
                  <DataTable rows={submissionRows} headers={submissionHeaders}>
                    {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                      <TableContainer
                        title={`Submissions for: ${selectedAssignment.title}`}
                        description={`${submissions.length} total submissions`}
                      >
                        <Table {...getTableProps()}>
                          <TableHead>
                            <TableRow>
                              {headers.map(header => (
                                <TableHeader {...getHeaderProps({ header })} key={header.key}>
                                  {header.header}
                                </TableHeader>
                              ))}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.map(row => (
                              <TableRow {...getRowProps({ row })} key={row.id}>
                                {row.cells.map(cell => {
                                  if (cell.info.header === 'status') {
                                    return (
                                      <TableCell key={cell.id}>
                                        <Tag
                                          type={
                                            cell.value === 'graded' ? 'green' : cell.value === 'late' ? 'red' : 'blue'
                                          }
                                        >
                                          {cell.value}
                                        </Tag>
                                      </TableCell>
                                    );
                                  }
                                  if (cell.info.header === 'actions') {
                                    return (
                                      <TableCell key={cell.id}>
                                        <Button kind="ghost" size="sm" renderIcon={View}>
                                          Grade
                                        </Button>
                                      </TableCell>
                                    );
                                  }
                                  return <TableCell key={cell.id}>{cell.value}</TableCell>;
                                })}
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </DataTable>
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>

          {/* Create Assignment Modal */}
          <Modal
            open={showCreateModal}
            onRequestClose={() => setShowCreateModal(false)}
            onRequestSubmit={createAssignment}
            modalHeading="Create New Assignment"
            primaryButtonText="Create Assignment"
            secondaryButtonText="Cancel"
            size="lg"
          >
            <div style={{ marginBottom: '1rem' }}>
              <TextInput
                id="title"
                labelText="Assignment Title"
                placeholder="Enter assignment title"
                value={formData.title}
                onChange={e => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <TextArea
                labelText="Description"
                placeholder="Brief description of the assignment"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <TextArea
                labelText="Instructions"
                placeholder="Detailed instructions for students"
                value={formData.instructions}
                onChange={e => setFormData({ ...formData, instructions: e.target.value })}
                rows={4}
              />
            </div>

            <Grid>
              <Column lg={8}>
                <Select
                  id="subject"
                  labelText="Subject"
                  value={formData.subject}
                  onChange={e => setFormData({ ...formData, subject: e.target.value })}
                >
                  <SelectItem value="math" text="Mathematics" />
                  <SelectItem value="science" text="Science" />
                  <SelectItem value="english" text="English" />
                  <SelectItem value="history" text="History" />
                  <SelectItem value="art" text="Art" />
                  <SelectItem value="music" text="Music" />
                  <SelectItem value="pe" text="Physical Education" />
                </Select>
              </Column>
              <Column lg={8}>
                <Select
                  id="grade_level"
                  labelText="Grade Level"
                  value={formData.grade_level}
                  onChange={e => setFormData({ ...formData, grade_level: e.target.value })}
                >
                  {[...Array(12)].map((_, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)} text={`Grade ${i + 1}`} />
                  ))}
                </Select>
              </Column>
            </Grid>

            <Grid style={{ marginTop: '1rem' }}>
              <Column lg={8}>
                <Select
                  id="assignment_type"
                  labelText="Assignment Type"
                  value={formData.assignment_type}
                  onChange={e => setFormData({ ...formData, assignment_type: e.target.value })}
                >
                  <SelectItem value="homework" text="Homework" />
                  <SelectItem value="quiz" text="Quiz" />
                  <SelectItem value="test" text="Test" />
                  <SelectItem value="project" text="Project" />
                  <SelectItem value="essay" text="Essay" />
                  <SelectItem value="lab" text="Lab Work" />
                  <SelectItem value="presentation" text="Presentation" />
                </Select>
              </Column>
              <Column lg={8}>
                <NumberInput
                  id="points"
                  label="Total Points"
                  value={formData.points}
                  onChange={(e: any, state: any) => setFormData({ ...formData, points: state.value })}
                  min={0}
                  max={1000}
                  step={5}
                />
              </Column>
            </Grid>

            <Grid style={{ marginTop: '1rem' }}>
              <Column lg={8}>
                <DatePicker
                  datePickerType="single"
                  onChange={(dates: Date[]) => {
                    if (dates.length > 0) {
                      setFormData({ ...formData, start_date: dates[0].toISOString().split('T')[0] });
                    }
                  }}
                >
                  <DatePickerInput
                    id="start_date"
                    labelText="Start Date"
                    placeholder="mm/dd/yyyy"
                    value={formData.start_date}
                  />
                </DatePicker>
              </Column>
              <Column lg={8}>
                <DatePicker
                  datePickerType="single"
                  onChange={(dates: Date[]) => {
                    if (dates.length > 0) {
                      setFormData({ ...formData, due_date: dates[0].toISOString().split('T')[0] });
                    }
                  }}
                >
                  <DatePickerInput
                    id="due_date"
                    labelText="Due Date"
                    placeholder="mm/dd/yyyy"
                    value={formData.due_date}
                  />
                </DatePicker>
              </Column>
            </Grid>

            <div style={{ marginTop: '1rem' }}>
              <RadioButtonGroup
                legendText="Submission Type"
                name="submission_type"
                valueSelected={formData.submission_type}
                onChange={value => setFormData({ ...formData, submission_type: value })}
              >
                <RadioButton labelText="Online" value="online" />
                <RadioButton labelText="Paper" value="paper" />
                <RadioButton labelText="Both" value="both" />
              </RadioButtonGroup>
            </div>

            <div style={{ marginTop: '1rem' }}>
              <Checkbox
                id="allow_late"
                labelText="Allow late submissions"
                checked={formData.allow_late}
                onChange={(_, { checked }) => setFormData({ ...formData, allow_late: checked })}
              />
            </div>

            <div style={{ marginTop: '1rem' }}>
              <FileUploader
                labelTitle="Attachments"
                labelDescription="Max file size is 10MB. Supported formats: PDF, DOC, DOCX, Images"
                buttonLabel="Add files"
                filenameStatus="edit"
                accept={['.pdf', '.doc', '.docx', '.png', '.jpg', '.jpeg']}
                multiple
                onChange={e => {
                  const files = Array.from(e.target.files || []);
                  setFormData({ ...formData, attachments: files });
                }}
              />
            </div>
          </Modal>
        </div>
      </Column>
    </Grid>
  );
};

export default TeacherAssignmentBuilder;
